<script setup lang="ts">
import { UiAppButton } from "@packages/component-library";
import { ref } from "vue";
import { useIntersectionObserver, useWindowSize } from "@vueuse/core";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const target = ref<any>(null);
const targetIsVisible = ref(false);
const { width } = useWindowSize();

useIntersectionObserver(
  target,
  ([{ isIntersecting }]) => {
    targetIsVisible.value = isIntersecting;
  },
  { threshold: 0.9 }
);
</script>

<template>
  <section id="apps" class="landing-app container">
    <div ref="target" class="landing-app__banner">
      <div class="landing-app__image">
        <img
          v-if="width > 1239"
          src="@/assets/images/landing/landing-apps.png"
          alt=""
        />
        <img
          v-if="width < 1240"
          src="@/assets/images/landing/landing-app.png"
          alt=""
        />
        <img
          src="@/assets/images/landing/elements/coin.svg"
          class="banner-animation"
          :class="{ 'banner-animation_active': targetIsVisible }"
          alt=""
        />
        <img
          src="@/assets/images/landing/elements/heart.svg"
          class="banner-animation"
          :class="{ 'banner-animation_active': targetIsVisible }"
          alt=""
        />
        <img
          src="@/assets/images/landing/elements/rays.svg"
          class="banner-animation"
          :class="{ 'banner-animation_active': targetIsVisible }"
          alt=""
        />
        <img
          src="@/assets/images/landing/elements/percent.svg"
          class="banner-animation"
          :class="{ 'banner-animation_active': targetIsVisible }"
          alt=""
        />
      </div>
    </div>

    <h3>{{ t("home.app.title") }}</h3>

    <p>
      {{ t("home.app.subTitle") }}
    </p>

    <div class="landing-app__links">
      <UiAppButton
        store="android"
        color="dark"
        size="l"
        link="https://play.google.com/store/apps/details?id=com.royalclub.rcpay&hl=ru"
      />
      <UiAppButton
        store="ru-store"
        color="dark"
        size="l"
        link="https://www.rustore.ru/catalog/app/com.royalclub.rcpay"
      />
      <UiAppButton
        store="ios"
        color="dark"
        size="l"
        link="https://apps.apple.com/us/app/rc-pay/id1553062417"
      />
    </div>
  </section>
</template>

<style scoped lang="scss">
.landing-app {
  &__banner {
    border-radius: 24px;
    background-color: var(--color-non-semantic-background-soft-yellow);
    height: 340px;
    display: flex;
    justify-content: center;
    align-items: flex-end;

    @media (max-width: 1239px) {
      height: 270px;
    }
  }

  &__image {
    width: 572px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    @media (max-width: 1239px) {
      width: 274px;
    }

    > img {
      &:first-child {
        width: 410px;

        @media (max-width: 1239px) {
          width: 214px;
        }
      }

      &:not(:first-child) {
        position: absolute;
      }

      &:nth-child(2) {
        left: 0;
        top: 64px;

        @media (max-width: 1239px) {
          width: 16px;
        }
      }

      &:nth-child(3) {
        bottom: 80px;
        left: 34px;

        @media (max-width: 1239px) {
          bottom: 40px;
          left: 5px;
        }
      }

      &:nth-child(4) {
        top: 10px;
        right: 34px;

        @media (max-width: 1239px) {
          top: -10px;
          right: 20px;
        }
      }

      &:nth-child(5) {
        bottom: 40px;
        right: 0;

        @media (max-width: 1239px) {
          width: 15px;
          right: 5px;
        }
      }
    }
  }

  > h3 {
    @include h3();
    text-align: center;
    margin: calc($base-space * 16) 0 0;
    color: var(--color-semantic-content-normal-primary);

    @media (max-width: 1279px) {
      margin: calc($base-space * 12) 0 0;
    }
  }

  > p {
    @include subtitle();
    text-align: center;
    margin: calc($base-space * 3) 0 0;
    color: var(--color-semantic-content-normal-secondary);

    @media (max-width: 1279px) {
      margin: calc($base-space * 2) 0 0;
    }
  }

  &__links {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: calc($base-space * 4);
    margin: calc($base-space * 12) 0 0;

    @media (max-width: 1279px) {
      margin: calc($base-space * 8) 0 0;
    }
  }
}

.banner-animation {
  opacity: 0;
  transition: 0.5s ease-in;

  &_active {
    opacity: 1;
  }
}
</style>
